import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Bookmark,
  Calendar,
  Gauge,
  Fuel,
  Settings2,
  Car,
  ArrowUpRight,
  ShipWheel,
  Palette,
} from 'lucide-react';
import { secureGetItem, secureSetItem } from '@/lib/storage';
import { generateVehicleSlug } from '@/lib/slug';
import { generateResponsiveImageUrls, getImageLoadingStrategy } from '@/lib/imageOptimization';
import './home.css';
import './inventory.css';

interface Vehicle {
  id: number;
  brand: string;
  model: string;
  version: string;
  price: number;
  photos: string[];
  mileage: number;
  fuel: string;
  modelYear: number;
  manufactureYear?: number;
  transmission: string;
  isZeroKm: boolean;
  plaque?: string;
  powerType?: string;
  color?: string;
  changed?: { featured?: boolean };
}

interface FeaturedListingCardProps {
  vehicle: Vehicle;
  variant?: 'card' | 'row';
}

const formatPrice = (price: number) =>
  new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL', maximumFractionDigits: 0 }).format(price);

const formatPriceParts = (price: number) => {
  const formatted = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 0 }).format(price);
  return { currency: 'R$', amount: formatted };
};

const formatMileage = (mileage: number) => {
  if (mileage === 0) return 'Zero KM';
  return `${new Intl.NumberFormat('pt-BR').format(mileage)} km`;
};

/** Quilometragem no estilo da listagem (sem sufixo km). */
const formatMileageCompact = (mileage: number) => {
  if (mileage === 0) return '0';
  return new Intl.NumberFormat('pt-BR').format(mileage);
};

const shortenTransmission = (value: string) => {
  if (!value?.trim()) return '—';
  return value
    .replace(/autom[aá]tico/i, 'Automático')
    .replace(/manual/i, 'Manual')
    .trim();
};

function getPromoBadge(vehicle: Vehicle): { label: string; variant: 'blue' | 'green' } | null {
  if (vehicle.changed?.featured) {
    return { label: 'Ótimo preço', variant: 'green' };
  }
  if (vehicle.isZeroKm || vehicle.mileage === 0) {
    return { label: 'Zero KM', variant: 'blue' };
  }
  if (vehicle.mileage > 0 && vehicle.mileage < 50000) {
    return { label: 'Baixa km', variant: 'blue' };
  }
  return null;
}

function formatYearRange(vehicle: Vehicle) {
  if (vehicle.manufactureYear && vehicle.modelYear && vehicle.manufactureYear !== vehicle.modelYear) {
    return `${vehicle.manufactureYear} - ${vehicle.modelYear}`;
  }
  return vehicle.modelYear ? String(vehicle.modelYear) : '—';
}

/** Último dígito da placa (rodízio) — só exibe se houver placa. */
function getPlateFinal(plaque?: string): string | null {
  if (!plaque?.trim()) return null;
  const digits = plaque.replace(/\D/g, '');
  if (!digits) return null;
  return digits[digits.length - 1];
}

function formatSpecValue(value: string) {
  return value.trim().toUpperCase();
}

export function FeaturedListingCard({ vehicle, variant = 'card' }: FeaturedListingCardProps) {
  const [isFavorited, setIsFavorited] = useState(false);
  const [vehicleSlug, setVehicleSlug] = useState(() => {
    const brand = (vehicle.brand || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    const model = (vehicle.model || '').toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-z0-9]+/g, '-');
    return `carro/${brand}-${model}-${vehicle.modelYear || vehicle.id}-${vehicle.id}`;
  });

  const promoBadge = getPromoBadge(vehicle);
  const plateFinal = getPlateFinal(vehicle.plaque);
  const imageUrl = vehicle.photos?.[0];
  const optimized = imageUrl
    ? generateResponsiveImageUrls(imageUrl, variant === 'row' ? 640 : 360, variant === 'row' ? 400 : 220)
    : null;
  const loadingStrategy = getImageLoadingStrategy(true, false);

  useEffect(() => {
    secureGetItem('favorites').then((favorites) => {
      if (favorites && Array.isArray(favorites)) {
        setIsFavorited(favorites.includes(vehicle.id));
      }
    }).catch(() => {});

    generateVehicleSlug(vehicle).then(setVehicleSlug).catch(() => {});
  }, [vehicle]);

  const toggleFavorite = async (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    const favorites = (await secureGetItem('favorites')) || [];
    let updated = Array.isArray(favorites) ? [...favorites] : [];
    if (updated.includes(vehicle.id)) {
      updated = updated.filter((id) => id !== vehicle.id);
      setIsFavorited(false);
    } else {
      updated.push(vehicle.id);
      setIsFavorited(true);
    }
    await secureSetItem('favorites', updated);
    window.dispatchEvent(new Event('favoritesUpdated'));
  };

  const specs = [
    { icon: Gauge, value: formatMileage(vehicle.mileage) },
    { icon: Fuel, value: vehicle.fuel || '—' },
    { icon: Settings2, value: shortenTransmission(vehicle.transmission) },
    { icon: Calendar, value: vehicle.modelYear ? String(vehicle.modelYear) : '—' },
  ];

  const rowSpecs: { icon: typeof Gauge; label: string; value: string }[] = [
    {
      icon: Gauge,
      label: 'Quilometragem:',
      value: formatMileageCompact(vehicle.mileage),
    },
    {
      icon: Settings2,
      label: 'Câmbio:',
      value: formatSpecValue(shortenTransmission(vehicle.transmission)),
    },
    {
      icon: Fuel,
      label: 'Combustível:',
      value: formatSpecValue(vehicle.fuel || '—'),
    },
  ];

  if (vehicle.powerType?.trim() && /el[eé]tric|hidr[aá]ul|mec[aâ]nic|assist/i.test(vehicle.powerType)) {
    rowSpecs.push({
      icon: ShipWheel,
      label: 'Tipo de Direção:',
      value: formatSpecValue(vehicle.powerType),
    });
  } else if (vehicle.color?.trim()) {
    rowSpecs.push({
      icon: Palette,
      label: 'Cor:',
      value: formatSpecValue(vehicle.color),
    });
  }

  const isRow = variant === 'row';
  const fullTitle = [vehicle.brand, vehicle.model, vehicle.version].filter(Boolean).join(' ');
  const priceParts = formatPriceParts(vehicle.price);

  if (isRow) {
    return (
      <article className="wl7-list-card">
        <Link to={`/estoque/${vehicleSlug}`} className="wl7-list-card-link">
          <div className="wl7-list-card-media">
            <button
              type="button"
              className={`wl7-featured-save${isFavorited ? ' is-active' : ''}`}
              onClick={toggleFavorite}
              aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar veículo'}
            >
              <Bookmark className="h-4 w-4" fill={isFavorited ? 'currentColor' : 'none'} />
            </button>
            {optimized ? (
              <img
                src={optimized.src}
                srcSet={optimized.srcSet}
                sizes="(max-width: 768px) 100vw, 340px"
                alt={fullTitle}
                className="wl7-list-card-img"
                loading={loadingStrategy.loading}
                decoding="async"
                width={optimized.width}
                height={optimized.height}
              />
            ) : (
              <div className="wl7-list-card-img wl7-featured-card-img--empty">
                <Car className="h-10 w-10 opacity-35" />
              </div>
            )}
            {plateFinal && (
              <span className="wl7-list-card-plate">Placa final {plateFinal}</span>
            )}
          </div>

          <div className="wl7-list-card-body">
            <span className="wl7-list-card-price-ribbon" aria-label={`Preço ${formatPrice(vehicle.price)}`}>
              <span className="wl7-list-card-price-currency">{priceParts.currency}</span>
              <span className="wl7-list-card-price-amount">{priceParts.amount}</span>
            </span>

            <h3 className="wl7-list-card-title">{fullTitle}</h3>
            <p className="wl7-list-card-year">{formatYearRange(vehicle)}</p>

            <div className="wl7-list-card-specs">
              {rowSpecs.map(({ icon: Icon, label, value }) => (
                <div key={label} className="wl7-list-card-spec">
                  <Icon className="wl7-list-card-spec-icon" aria-hidden />
                  <span className="wl7-list-card-spec-label">{label}</span>
                  <span className="wl7-list-card-spec-value">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </Link>
      </article>
    );
  }

  return (
    <article className="wl7-featured-card">
      <Link to={`/estoque/${vehicleSlug}`} className="wl7-featured-card-link">
        <div className="wl7-featured-card-media">
          {promoBadge && (
            <span className={`wl7-featured-badge wl7-featured-badge--${promoBadge.variant}`}>
              {promoBadge.label}
            </span>
          )}
          <button
            type="button"
            className={`wl7-featured-save${isFavorited ? ' is-active' : ''}`}
            onClick={toggleFavorite}
            aria-label={isFavorited ? 'Remover dos favoritos' : 'Salvar veículo'}
          >
            <Bookmark className="h-4 w-4" fill={isFavorited ? 'currentColor' : 'none'} />
          </button>
          {optimized ? (
            <img
              src={optimized.src}
              srcSet={optimized.srcSet}
              sizes="(max-width: 768px) 85vw, 300px"
              alt={`${vehicle.brand} ${vehicle.model}`}
              className="wl7-featured-card-img"
              loading={loadingStrategy.loading}
              decoding="async"
              width={optimized.width}
              height={optimized.height}
            />
          ) : (
            <div className="wl7-featured-card-img wl7-featured-card-img--empty">
              <Car className="h-10 w-10 opacity-35" />
            </div>
          )}
        </div>
        <div className="wl7-featured-card-body">
          <h3 className="wl7-featured-card-title">
            {vehicle.brand} {vehicle.model}
          </h3>
          {vehicle.version && (
            <p className="wl7-featured-card-subtitle">{vehicle.version}</p>
          )}
          <div className="wl7-featured-specs">
            {specs.map(({ icon: Icon, value }, index) => (
              <div key={index} className="wl7-featured-spec">
                <Icon className="wl7-featured-spec-icon" />
                <span>{value}</span>
              </div>
            ))}
          </div>
          <div className="wl7-featured-card-footer">
            <span className="wl7-featured-price">{formatPrice(vehicle.price)}</span>
            <span className="wl7-featured-cta">
              Ver detalhes
              <ArrowUpRight className="h-4 w-4" />
            </span>
          </div>
        </div>
      </Link>
    </article>
  );
}
